import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { useGetDeviceDetailsInStateStore } from '@unifyapps/carbon/no-code/stores/GlobalStateStore';
import { getCustomVariantDataAttributes } from '@unifyapps/carbon/no-code/utils/getCustomVariantDataAttributes';
import {
  getConditionalIconNode,
  getConditionalPropertyValue,
} from '@unifyapps/carbon/utils/conditionalValues';
import { getInlineSvgIcon } from '@unifyapps/carbon/utils/getInlineSvgIcon';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type {
  IconButtonBlockStateType,
  IconButtonComponentType,
} from '@unifyapps/defs/blocks/IconButton/types';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { Event } from '@unifyapps/defs/types/event';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { useLongPress } from '@unifyapps/hooks/useLongPress';

import { IconButton as BaseIconButton } from '@unifyapps/ui/components/IconButton';
import type { SyntheticEvent } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useBlockTooltip } from '../common/hooks/useBlockTooltip';
import { markEventAsHandledByBlock } from '../utils/eventHandling';
import { normalizeIconButtonTooltip } from './utils/normalizeIconButtonTooltip';

/** all known IconButton Appearance keys — anything else is a custom variant dimension */
const KNOWN_APPEARANCE_KEYS = new Set([
  'color',
  'size',
  'variant',
  'styles',
  'disabled',
  'loading',
]);

const useGetBlockInteractions = ({ events, blockId }: { events?: Event[]; blockId: string }) => {
  const { device } = useGetDeviceDetailsInStateStore();
  const { emitEvent } = useBlockEvents(events, blockId);

  const hasLongPressEvent = events?.some((event) => event.eventType === EventTypeEnum.OnLongPress);
  const allowLongPressEvent =
    (device === DeviceVariantType.MOBILE || device === DeviceVariantType.TABLET) &&
    hasLongPressEvent;

  const onLongPress = useCallback(() => {
    void emitEvent({ eventType: EventTypeEnum.OnLongPress });
  }, [emitEvent]);

  const { longPressOccurredRef, longPressProps } = useLongPress(onLongPress);

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      // prevent click if long press just occurred
      if (longPressOccurredRef.current) {
        longPressOccurredRef.current = false;
        return;
      }
      // Mark event as handled by this block so parent components (e.g., Card header)
      // know not to react to it. This replaces stopPropagation to allow event bubbling
      // while still preventing double-handling in the no-code system.
      markEventAsHandledByBlock({ blockType: 'IconButton', event: e });

      // stop propagation if OnClick event is configured, this helps preventing the select item event being fired from repeatable block
      if (events?.find(({ eventType }) => eventType === EventTypeEnum.OnClick)) {
        e.stopPropagation();
      }

      void emitEvent({ eventType: EventTypeEnum.OnClick, domEvent: e });
    },
    [emitEvent, longPressOccurredRef, events],
  );

  return { onClick, longPressProps: allowLongPressEvent ? longPressProps : undefined };
};

function IconButton({
  attributesAndEventListeners,
  events,
  computedBlockState,
  dndRef,
  block,
  component,
  registry,
}: BlockComponentProps<
  IconButtonComponentType,
  IconButtonBlockStateType,
  IconButtonBlockStateType
>) {
  const { content, appearance } = computedBlockState;
  const { slots } = component;
  const BlockRenderer = registry.getBlockRenderer();
  const color = getConditionalPropertyValue({ value: appearance?.color }) ?? 'brand';
  const loading = getConditionalPropertyValue({ value: appearance?.loading }) ?? false;

  const { icon, addOns } = content;

  const { className, style: appearanceStyle } = useBlockAppearanceStyles({
    appearanceStyles: appearance?.styles,
  });

  const { finalStyle: style } = useCustomStyles({
    customCSS: block.additional?.customCSS,
    customStyles: block.component?.appearance?.customStyles,
    style: appearanceStyle,
  });

  const { onClick, longPressProps } = useGetBlockInteractions({
    events,
    blockId: computedBlockState.id,
  });

  const Icon = getInlineSvgIcon(icon);
  const IconNode = getConditionalIconNode(icon, appearance?.size);

  // custom variant dimensions (e.g. radius → data-radius="pill") applied as data attributes
  const customVariantDataAttrs = useMemo(
    () =>
      getCustomVariantDataAttributes(
        appearance as Record<string, unknown> | undefined,
        KNOWN_APPEARANCE_KEYS,
      ),
    [appearance],
  );

  // aria-label priority: tooltip content > icon name
  const ariaLabel = addOns?.tooltip?.content ?? getConditionalPropertyValue({ value: icon }) ?? '';

  const tooltipAddOn = useMemo(() => normalizeIconButtonTooltip(addOns?.tooltip), [addOns?.tooltip]);

  const iconButtonEl = (
    <BaseIconButton
      aria-label={ariaLabel}
      className={className}
      color={color}
      disabled={appearance?.disabled}
      Icon={Icon}
      IconNode={IconNode}
      loading={loading}
      onClick={onClick}
      ref={dndRef}
      size={appearance?.size ?? 'md'}
      style={style}
      variant={appearance?.variant ?? 'solid'}
      {...longPressProps}
      {...attributesAndEventListeners}
      {...customVariantDataAttrs}
    />
  );

  const { wrapWithTooltip, tooltipBuilderNode } = useBlockTooltip({
    blockId: computedBlockState.id,
    tooltipAddOn,
    tooltipSlotBlockId: slots?.tooltip?.blockId,
    BlockRenderer,
  });

  // hooks must run every render, so the "no icon" bail-out sits after useBlockTooltip
  if (!Icon && !IconNode) {
    return null;
  }

  return (
    <>
      {wrapWithTooltip(iconButtonEl)}
      {tooltipBuilderNode}
    </>
  );
}

export default memo(IconButton);
